import { StateCreator } from 'zustand';
import { PanelDefinition } from '@/core';
import { Middleware } from './common';
import { DashboardStoreState } from './DashboardProvider';
import { PanelGroupDefinition, PanelGroupSlice } from './panel-group-slice';
import { PanelSlice } from './panel-slice';

export const DASHBOARD_BASELINE_ACTION_NAME = 'Dashboard state saved';
export const DASHBOARD_BASELINE_ACTION_ID = 'dashboard-initial-state';

export interface DashboardSnapshot {
  panels: Record<string, PanelDefinition>;
  panelGroups: Record<number, PanelGroupDefinition>;
  panelGroupOrder: number[];
  timestamp: number;
  actionId: string;
  actionName: string;
}

export interface DashboardAction {
  actionId: string;
  actionName?: string;
}

export interface HistorySlice {
  history: DashboardSnapshot[];
  currentIndex: number;
  maxHistorySize: number;
  pushToHistory: (action: DashboardAction) => void;
  initializeHistoryIfEmpty: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearActions: () => void;
  getLastAction: () => DashboardAction | undefined;
}

export function createHistorySlice(
  maxHistorySize = 10
): StateCreator<HistorySlice & PanelSlice & PanelGroupSlice & DashboardStoreState, Middleware, [], HistorySlice> {
  return (set, get) => ({
    history: [],
    currentIndex: -1,
    maxHistorySize,

    pushToHistory: ({ actionId, actionName }: DashboardAction) => {
      const state = get();
      const snapshot: DashboardSnapshot = {
        panels: { ...state.panels },
        panelGroups: { ...state.panelGroups },
        panelGroupOrder: [...state.panelGroupOrder],
        timestamp: Date.now(),
        actionId,
        actionName: actionName ?? actionId,
      };

      set(
        (state) => {
          // Remove any history after current index (for redo)
          const newHistory = state.history.slice(0, state.currentIndex + 1);
          newHistory.push(snapshot);

          // Trim history if exceeds max size
          if (newHistory.length > state.maxHistorySize) {
            newHistory.shift();
          }

          state.history = newHistory;
          state.currentIndex = newHistory.length - 1;
        },
        false,
        '[History] pushToHistory'
      );
    },

    initializeHistoryIfEmpty: () => {
      const state = get();

      // If history is empty, create a baseline snapshot first
      if (state.history.length === 0) {
        const baselineSnapshot: DashboardSnapshot = {
          panels: { ...state.panels },
          panelGroups: { ...state.panelGroups },
          panelGroupOrder: [...state.panelGroupOrder],
          timestamp: Date.now(),
          actionId: DASHBOARD_BASELINE_ACTION_ID,
          actionName: DASHBOARD_BASELINE_ACTION_NAME,
        };

        set(
          (state) => {
            state.history = [baselineSnapshot];
            state.currentIndex = 0;
          },
          false,
          `[History] initializeHistoryIfEmpty - ${DASHBOARD_BASELINE_ACTION_NAME}`
        );
      }
    },

    undo: () => {
      const state = get();
      if (!state.canUndo()) return;

      const targetIndex = state.currentIndex - 1;
      const snapshot = state.history[targetIndex];

      if (!snapshot) return;

      set(
        (state) => {
          state.panels = { ...snapshot.panels };
          state.panelGroups = { ...snapshot.panelGroups };
          state.panelGroupOrder = [...snapshot.panelGroupOrder];
          state.currentIndex = targetIndex;
        },
        false,
        '[History] undo'
      );
    },

    redo: () => {
      const state = get();
      if (!state.canRedo()) return;

      const targetIndex = state.currentIndex + 1;
      const snapshot = state.history[targetIndex];

      if (!snapshot) return;

      set(
        (state) => {
          state.panels = { ...snapshot.panels };
          state.panelGroups = { ...snapshot.panelGroups };
          state.panelGroupOrder = [...snapshot.panelGroupOrder];
          state.currentIndex = targetIndex;
        },
        false,
        '[History] redo'
      );
    },

    canUndo: () => {
      const state = get();
      return state.history.length > 0 && state.currentIndex > 0;
    },

    canRedo: () => {
      const state = get();
      return state.history.length > 0 && state.currentIndex < state.history.length - 1;
    },

    clearActions: () => {
      set(
        (state) => {
          state.history = [];
          state.currentIndex = -1;
        },
        false,
        '[History] clearActions'
      );
    },

    getLastAction: () => {
      const state = get();
      if (state.currentIndex >= 0 && state.currentIndex < state.history.length) {
        const snapshot = state.history[state.currentIndex];
        if (!snapshot) return undefined;
        return { actionId: snapshot.actionId, actionName: snapshot?.actionName };
      }
      return undefined;
    },
  });
}
